import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext(null);

const DEFAULTS = {
    app_name: "Law OS",
    app_owner: "Inno Aim Private Limited",
    app_tagline: "Cabinet-grade Legal OS for advocates and law firms",
    logo_url: "",
    favicon_url: "",
    og_image_url: "",
    app_icon_url: "",
    logo_display_height: 32,
    logo_layout: "icon_and_text",
};

export function BrandingProvider({ children }) {
    const [branding, setBranding] = useState(DEFAULTS);

    const reload = async () => {
        try {
            const { data } = await api.get("/platform/branding");
            setBranding({ ...DEFAULTS, ...data });
        } catch {
            setBranding(DEFAULTS);
        }
    };

    useEffect(() => { reload(); }, []);

    // Sync the browser tab — favicon, page title, OG meta tags — to the live
    // branding so super-admin updates take effect immediately.
    useEffect(() => {
        const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
        const bust = (url) => url ? `?v=${encodeURIComponent(url.slice(-20))}` : "";

        // Title
        if (branding.app_name) document.title = branding.app_name;

        // Favicon — fall back to logo if no favicon uploaded
        const iconUrl = branding.favicon_url
            ? `${BACKEND}/api/platform/branding/favicon${bust(branding.favicon_url)}`
            : (branding.logo_url ? `${BACKEND}/api/platform/branding/logo${bust(branding.logo_url)}` : "");
        if (iconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
            link.href = iconUrl;
        }

        // OG / Twitter meta (best-effort — many crawlers fetch SSR, but client-side
        // helps in-app share sheets and Slack unfurls for app-deep links).
        const setMeta = (selector, attr, name, content) => {
            if (!content) return;
            let el = document.head.querySelector(selector);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
            el.setAttribute("content", content);
        };
        const ogImg = branding.og_image_url ? `${BACKEND}/api/platform/branding/og-image${bust(branding.og_image_url)}` : iconUrl;
        setMeta('meta[property="og:title"]', "property", "og:title", branding.app_name);
        setMeta('meta[property="og:description"]', "property", "og:description", branding.app_tagline);
        setMeta('meta[property="og:image"]', "property", "og:image", ogImg);
        setMeta('meta[property="og:site_name"]', "property", "og:site_name", branding.app_name);
        setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
        setMeta('meta[name="twitter:title"]', "name", "twitter:title", branding.app_name);
        setMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImg);
        setMeta('meta[name="description"]', "name", "description", branding.app_tagline);
    }, [branding]);

    const value = useMemo(() => {
        const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
        const bust = (url) => url ? `?v=${encodeURIComponent(url.slice(-20))}` : "";
        return {
            ...branding,
            logo_src: branding.logo_url ? `${BACKEND}/api/platform/branding/logo${bust(branding.logo_url)}` : "",
            favicon_src: branding.favicon_url ? `${BACKEND}/api/platform/branding/favicon${bust(branding.favicon_url)}` : "",
            og_image_src: branding.og_image_url ? `${BACKEND}/api/platform/branding/og-image${bust(branding.og_image_url)}` : "",
            app_icon_src: branding.app_icon_url ? `${BACKEND}/api/platform/branding/app-icon${bust(branding.app_icon_url)}` : "",
            reload,
        };
    }, [branding]);

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
    return useContext(BrandingContext) || { ...DEFAULTS, logo_src: "", reload: () => {} };
}
